import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // send the httpOnly refreshToken cookie for /api/auth/refresh
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function forceLogout() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('user');
  // Avoid redirect loop: only redirect if not already on login page
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
    toast.error('Session expired. Please login again.');
  }
}

// Access tokens are short-lived (15m); a 7-day refreshToken cookie backs
// silent renewal via /api/auth/refresh. Concurrent 401s share one in-flight
// refresh call instead of each triggering their own.
let refreshPromise = null;
function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const newToken = res.data.accessToken;
        localStorage.setItem('accessToken', newToken);
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Response interceptor to handle 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401) {
      // Only treat this as a session expiry if the request actually carried a token.
      // Guests browsing public pages routinely trigger 401s from optional,
      // try/catch-wrapped calls (e.g. a "is this user premium?" check) — those
      // should fail silently, not force-redirect them off the page they're on.
      const hadToken = !!originalRequest?.headers?.Authorization;
      if (hadToken && !originalRequest._retried) {
        originalRequest._retried = true;
        try {
          const newToken = await refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch {
          // Refresh token itself is invalid/expired — no way to recover silently
          forceLogout();
        }
      } else if (hadToken) {
        forceLogout();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
